'use client';

// ADR-070 §1 (refresh proactivo) — SPEC-WEBSOCKET-RECONNECT §3 PR 1.
// Anti-§6: reusa tokenStore + endpoint /auth/refresh existentes.
// 0 lógica inventada.
//
// Comportamiento (SPEC §3 PR 1):
// - Cada 30s verifica el TTL del access token actual (jwtDecode — no
//   verifica firma, solo lee `exp`).
// - Si TTL < 60s (token expira en <1 min), llama a `tryRefresh()` (el
//   MISMO helper que usa `apiFetch` on 401 — single source of truth).
//   Esto evita la race condition donde dos refresh paths paralelos
//   (`useTokenRefresh` con su propio fetch + `tryRefresh` reactivo)
//   colisionaban: el backend revoca el refresh token viejo tras cada
//   refresh exitoso (rotación stateless), así que el segundo refresh
//   fallaba con "token revoked" → `setUser(null)` → logout forzado.
// - Mutex: `tryRefresh()` ya tiene su propio mutex (`refreshPromise` a
//   nivel módulo) — deduplica calls concurrentes. NO necesitamos
//   `refreshingRef` local.
// - Si refresh OK → `tokenStore.setAccess(nuevo)` (lo hace `tryRefresh`
//   internamente) → el socket usa el token nuevo en el próximo reconnect.
// - Si refresh falla → best-effort (el socket fallback refresh maneja el
//   error). NO disparamos `profileia:session-expired` desde aquí (eso lo
//   hace `apiFetch` solo cuando un request genuino recibe 401 Y el
//   refresh falla — distinguir entre "sesión realmente expirada" vs
//   "refresh transitorio rate-limited").
//
// TASK-FEAT-JWT-RESILIENCE-FIX (2026-11-05): unified the proactive
// refresh path with the reactive path. Before this fix, `useTokenRefresh`
// made its own `fetch('/api/auth/refresh')` call with a SEPARATE mutex
// (`refreshingRef`). When both paths triggered concurrently (e.g., the
// 30s interval fires + a parallel API call gets 401), the backend's
// stateless refresh token rotation caused one of the two refreshes to
// fail (the old refresh token was revoked after the first refresh
// succeeded). The failed refresh path would then clear the token store
// + dispatch `profileia:session-expired` → forced logout. Now both
// paths share `tryRefresh()` → `refreshPromise` mutex deduplicates →
// no race.
//
// Montado UNA vez en el AuthProvider (root layout — patrón P-282). El hook
// es autónomo: CERO dependencia de React state externo.

import { useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { tokenStore, tryRefresh } from '@/lib/api-client';

export function useTokenRefresh() {
  useEffect(() => {
    // P-370 fix: extract the async body into a named function + wrap with
    // .catch(). The previous `setInterval(async () => {...})` pattern
    // returns a Promise that is not awaited — if any rejection escapes
    // the try/catch (defensive programming — the try/catch should catch
    // everything, but a future code change could introduce a path that
    // throws outside it), it surfaces as "Uncaught (in promise) undefined"
    // on the console. Wrapping with .catch() ensures no unhandled rejection
    // escapes the interval callback.
    const checkAndRefresh = async () => {
      const token = tokenStore.getAccess();
      if (!token) return;

      try {
        const payload = jwtDecode<{ exp: number }>(token);
        const expiresAtMs = payload.exp * 1000;
        const ttlMs = expiresAtMs - Date.now();

        // Refresh 60s antes de expirar. TASK-FEAT-JWT-RESILIENCE-FIX
        // (2026-11-05): delegate to `tryRefresh()` — the SAME helper that
        // `apiFetch` uses on 401. This shares the `refreshPromise` mutex
        // (module-level) so concurrent refresh attempts are deduplicated.
        // If a parallel API call already triggered a refresh, `tryRefresh()`
        // returns the existing promise → no second refresh → no race
        // condition with the backend's stateless token rotation.
        if (ttlMs < 60_000) {
          await tryRefresh();
          // `tryRefresh()` handles: tokenStore.setAccess(newToken) on
          // success, dispatching 'token-refreshed' event (for socket
          // reconnect), clearing `sessionExpired` flag, and the 60s
          // cooldown on 429/5xx. We DON'T need to do anything else here.
          //
          // If `tryRefresh()` returns false (refresh failed — rate
          // limited, network blip, cookie expired), we do NOT force
          // logout here. The user stays logged in as long as the
          // current access token is still valid by signature. A genuine
          // 401 from a real API call will trigger the proper logout
          // flow via `apiFetch`'s error handler.
        }
      } catch {
        // Token malformado — dejar que el socket-auth lo rechace
      }
    };
    const interval = setInterval(() => {
      void checkAndRefresh().catch(() => {
        // Defensive: ensures no unhandled rejection escapes the interval
      });
    }, 30_000); // check cada 30s

    return () => clearInterval(interval);
  }, []);
}
